import { Alert, Button, Input, Message, Radio, Space, Spin, Tag, Typography } from "@arco-design/web-react";
import { useEffect, useMemo, useState } from "react";

import type {
  CustomizationOpportunityBinding,
  CustomizationOpportunityBindingDecision,
  CustomizationOpportunityCandidate,
} from "../types";
import "./CustomizationOpportunityBindingContent.css";

const { Text } = Typography;

type Props = {
  binding: CustomizationOpportunityBinding;
  candidatesLoading?: boolean;
  canBindOpportunity: boolean;
  canCreateOpportunity: boolean;
  onResolve: (decision: CustomizationOpportunityBindingDecision) => Promise<unknown>;
  onClose: () => void;
};

const MATCHED_TAG_LIMIT = 6;

/** 归属决策对应的成功提示文案。 */
function resolveBindingSuccessMessage(
  decision: CustomizationOpportunityBindingDecision["decision"],
): string {
  if (decision === "create") {
    return "已创建独立商机并完成归属。";
  }
  if (decision === "retain") {
    return "已保留当前商机归属。";
  }
  return "已归属到所选商机。";
}

/** 渲染单条候选的标题、分数与命中/差异标签，避免再拼接冗长 matchSummary。 */
function OpportunityCandidateCardBody({
  candidate,
}: {
  candidate: CustomizationOpportunityCandidate;
}) {
  const matched = candidate.matchedDimensions ?? [];
  const different = candidate.differentDimensions ?? [];
  const hasDimensionTags = matched.length > 0 || different.length > 0;
  const visibleMatched = matched.slice(0, MATCHED_TAG_LIMIT);
  const hiddenMatchedCount = matched.length - visibleMatched.length;

  return (
    <span className="customization-opportunity-candidate-row">
      <span className="customization-opportunity-candidate-title">
        <strong className="customization-opportunity-candidate-code">
          {candidate.opportunityCode}
        </strong>
        <span className="customization-opportunity-candidate-name">{candidate.opportunityName}</span>
      </span>
      <span className="customization-opportunity-candidate-meta" aria-label="候选匹配摘要">
        <Tag size="small" color="arcoblue" className="customization-opportunity-candidate-score">
          {candidate.matchScore} 分
        </Tag>
        {candidate.linkedCustomizationCount > 0 ? (
          <Tag size="small">关联定制 {candidate.linkedCustomizationCount}</Tag>
        ) : null}
        {candidate.ownerUserName ? (
          <Text type="secondary" className="customization-opportunity-candidate-owner">
            负责人 {candidate.ownerUserName}
          </Text>
        ) : null}
      </span>
      {hasDimensionTags ? (
        <span className="customization-opportunity-candidate-dims" aria-label="匹配维度">
          {visibleMatched.map((dimension) => (
            <Tag key={`matched-${dimension}`} size="small" color="green">
              {dimension}
            </Tag>
          ))}
          {hiddenMatchedCount > 0 ? (
            <Text type="secondary" className="customization-opportunity-candidate-more">
              +{hiddenMatchedCount}
            </Text>
          ) : null}
          {different.map((dimension) => (
            <Tag key={`diff-${dimension}`} size="small" color="orangered">
              差异 · {dimension}
            </Tag>
          ))}
        </span>
      ) : candidate.matchSummary ? (
        <Text type="secondary" className="customization-opportunity-candidate-summary">
          {candidate.matchSummary}
        </Text>
      ) : null}
      {!candidate.selectable && candidate.unselectableReason ? (
        <Text type="error" className="customization-opportunity-candidate-blocked">
          不可选：{candidate.unselectableReason}
        </Text>
      ) : null}
    </span>
  );
}

/** PC Modal 与手机底部 Drawer 共用的商机归属业务内容。候选、排序、命中原因均来自后端 DTO。 */
export function CustomizationOpportunityBindingContent({
  binding,
  candidatesLoading = false,
  canBindOpportunity,
  canCreateOpportunity,
  onResolve,
  onClose,
}: Props) {
  const firstSelectable = useMemo(
    () => binding.candidates.find((candidate) => candidate.selectable)?.opportunityId,
    [binding.candidates],
  );
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<number | undefined>(undefined);
  const [decisionNote, setDecisionNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setSelectedOpportunityId(undefined);
    setDecisionNote("");
  }, [binding.projectUpdatedAt, binding.status, firstSelectable]);

  useEffect(() => {
    setSubmitError("");
  }, [binding.status, binding.demandId, firstSelectable]);

  if (!canBindOpportunity) {
    return (
      <div className="customization-opportunity-binding-content" aria-label="商机归属选择器">
        <Alert
          type="warning"
          showIcon
          content="当前定制尚未完成商机归属，请联系销售完成后再继续。"
        />
        <div className="customization-opportunity-binding-actions">
          <Button onClick={onClose}>知道了</Button>
        </div>
      </div>
    );
  }

  const isReview = binding.status === "review_required";
  const isCodeMismatch = binding.reasonCode === "opportunity_code_mismatch";
  const canRetain = isReview && Boolean(binding.currentOpportunity);
  const submit = async (decision: CustomizationOpportunityBindingDecision["decision"]) => {
    if (!canBindOpportunity || !binding.demandId || submitting || candidatesLoading) return;
    if (decision === "existing" && selectedOpportunityId === undefined) {
      const message = "请先选择要归属的商机。";
      setSubmitError(message);
      Message.error(message);
      return;
    }
    if (decision === "retain" && !decisionNote.trim()) {
      const message = "请填写复核原因。";
      setSubmitError(message);
      Message.error(message);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await onResolve({
        decision,
        opportunityId: decision === "existing" ? selectedOpportunityId : undefined,
        demandId: binding.demandId,
        expectedProjectUpdatedAt: String(binding.projectUpdatedAt ?? ""),
        decisionNote: decisionNote.trim() || undefined,
        idempotencyKey:
          decision === "create"
            ? `customization-binding-${binding.projectId}-${binding.demandId}`
            : undefined,
      });
      Message.success(resolveBindingSuccessMessage(decision));
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message.trim()
          : "商机归属失败，请稍后重试。";
      setSubmitError(message);
      Message.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="customization-opportunity-binding-content" aria-label="商机归属选择器">
      <Text type="secondary" className="customization-opportunity-binding-hint">
        {isCodeMismatch
          ? "项目保存的商机编号与商机主数据不一致，请重新确认归属，系统会自动修正编号。"
          : isReview
          ? "学校/组织或联系人已变化，请确认是否保留当前商机，或重新归属。"
          : "V1 已完整，请先确认商机归属后继续后续业务。"}
      </Text>
      {binding.currentOpportunity ? (
        <div className="customization-opportunity-current" aria-label="当前商机">
          <strong>当前商机</strong>
          <span>{binding.currentOpportunity.opportunityCode}</span>
          <span>{binding.currentOpportunity.opportunityName}</span>
          <Tag color="blue">{binding.currentOpportunity.status}</Tag>
        </div>
      ) : null}
      <div className="customization-opportunity-candidates" aria-label="商机候选">
        {candidatesLoading ? (
          <div className="customization-opportunity-candidates-loading" aria-label="商机候选加载中">
            <Spin tip="正在加载可选商机候选…" />
          </div>
        ) : binding.candidates.length ? (
          <>
            <Radio.Group
              className="customization-opportunity-candidate-group"
              disabled={!canBindOpportunity}
              value={selectedOpportunityId}
              onChange={(value) => setSelectedOpportunityId(Number(value))}
            >
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                {binding.candidates.map((candidate) => (
                  <Radio
                    key={candidate.opportunityId}
                    className="customization-opportunity-candidate-radio"
                    value={candidate.opportunityId}
                    disabled={!candidate.selectable}
                  >
                    <OpportunityCandidateCardBody candidate={candidate} />
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            {selectedOpportunityId !== undefined ? (
              <Button
                type="text"
                size="small"
                className="customization-opportunity-clear-selection"
                disabled={submitting}
                onClick={() => setSelectedOpportunityId(undefined)}
              >
                取消选择
              </Button>
            ) : null}
          </>
        ) : (
          <div className="customization-opportunity-candidates-empty" aria-label="暂无候选商机">
            <Text type="secondary">暂无可选候选商机。</Text>
            <Text type="secondary" className="customization-opportunity-candidates-empty-hint">
              可创建独立商机，或稍后处理。
            </Text>
          </div>
        )}
        {!candidatesLoading && binding.restrictedCandidateCount > 0 ? (
          <Text type="secondary" className="customization-opportunity-restricted">
            另有 {binding.restrictedCandidateCount} 条候选受权限限制。
          </Text>
        ) : null}
      </div>
      {canRetain ? (
        <Input.TextArea
          value={decisionNote}
          onChange={setDecisionNote}
          placeholder="请填写复核原因（必填）"
          maxLength={1000}
          showWordLimit
          autoSize={{ minRows: 2, maxRows: 4 }}
          disabled={candidatesLoading}
        />
      ) : null}
      {submitError ? (
        <div data-testid="opportunity-binding-submit-error">
          <Alert type="error" showIcon content={submitError} style={{ marginTop: 8 }} />
        </div>
      ) : null}
      <div className="customization-opportunity-binding-actions">
        <Button onClick={onClose} disabled={submitting}>
          稍后处理
        </Button>
        {canRetain ? (
          <Button
            onClick={() => void submit("retain")}
            loading={submitting}
            disabled={candidatesLoading || !decisionNote.trim()}
          >
            保留当前商机
          </Button>
        ) : null}
        <Button
          type="primary"
          onClick={() => void submit("existing")}
          loading={submitting}
          disabled={
            candidatesLoading || !canBindOpportunity || selectedOpportunityId === undefined
          }
        >
          归属已有商机
        </Button>
        {canCreateOpportunity ? (
          <Button
            onClick={() => void submit("create")}
            loading={submitting}
            disabled={candidatesLoading || !canBindOpportunity}
          >
            创建独立商机
          </Button>
        ) : null}
      </div>
    </div>
  );
}
